<template>
  <v-container class="subscription-container">
    <div class="pricing-header">
      <h1 class="pricing-title">
        <v-icon color="primary" size="32" class="mr-3">mdi-crown</v-icon>
        ARCM Premium Features
      </h1>
      <p class="pricing-subtitle">
        Erweitern Sie Ihr ARCM-System mit professionellen Features für optimierte Workflows
      </p>
    </div>

    <!-- Aktuelle Plan Anzeige -->
    <v-card class="current-plan-card mb-8" v-if="currentPlan">
      <v-card-title class="d-flex align-center">
        <v-icon :color="currentPlan.color" class="mr-2">{{ currentPlan.icon }}</v-icon>
        Aktueller Plan: {{ currentPlan.name }}
        <v-spacer></v-spacer>
        <v-chip :color="currentPlan.color" variant="elevated">
          {{ currentPlan.price }}
        </v-chip>
      </v-card-title>
      <v-card-text>
        <p>{{ currentPlan.description }}</p>
        <div class="d-flex align-center mt-2">
          <v-icon color="success" size="small" class="mr-1">mdi-calendar-check</v-icon>
          <span class="text-caption">Nächste Abrechnung: {{ nextBilling }}</span>
        </div>
      </v-card-text>
    </v-card>

    <!-- Pricing Cards -->
    <v-row class="pricing-cards">
      <!-- Basic Plan -->
      <v-col cols="12" md="4">
        <v-card
          class="pricing-card basic-card"
          :class="{ 'current-plan-border': currentPlan?.id === 'basic' }"
        >
          <div class="card-header basic-header">
            <v-icon color="grey" size="40">mdi-account</v-icon>
            <h3 class="plan-name">Basic</h3>
            <div class="plan-price">
              <span class="price-amount">Kostenlos</span>
            </div>
            <p class="plan-description">Perfekt für den Einstieg</p>
          </div>

          <v-divider></v-divider>

          <v-card-text class="features-list">
            <div class="feature-item">
              <v-icon color="success" size="small">mdi-check</v-icon>
              <span>Bis zu 50 Aufträge/Monat</span>
            </div>
            <div class="feature-item">
              <v-icon color="success" size="small">mdi-check</v-icon>
              <span>Basis-Statistiken</span>
            </div>
            <div class="feature-item">
              <v-icon color="success" size="small">mdi-check</v-icon>
              <span>Standard Support</span>
            </div>
            <div class="feature-item">
              <v-icon color="success" size="small">mdi-check</v-icon>
              <span>Mobile App Zugang</span>
            </div>
            <div class="feature-item disabled">
              <v-icon color="grey" size="small">mdi-close</v-icon>
              <span>Erweiterte Berichte</span>
            </div>
            <div class="feature-item disabled">
              <v-icon color="grey" size="small">mdi-close</v-icon>
              <span>API Zugang</span>
            </div>
            <div class="feature-item disabled">
              <v-icon color="grey" size="small">mdi-close</v-icon>
              <span>White-Label Option</span>
            </div>
          </v-card-text>

          <v-card-actions class="pa-4">
            <v-btn
              variant="outlined"
              color="grey"
              block
              :disabled="currentPlan?.id === 'basic'"
              @click="selectPlan('basic')"
            >
              {{ currentPlan?.id === 'basic' ? 'Aktueller Plan' : 'Auswählen' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>

      <!-- Professional Plan -->
      <v-col cols="12" md="4">
        <v-card
          class="pricing-card professional-card"
          :class="{ 'current-plan-border': currentPlan?.id === 'professional' }"
        >
          <div class="popular-badge">
            <v-chip color="amber" size="small" variant="elevated">
              <v-icon start size="small">mdi-star</v-icon>
              Beliebt
            </v-chip>
          </div>

          <div class="card-header professional-header">
            <v-icon color="primary" size="40">mdi-briefcase</v-icon>
            <h3 class="plan-name">Professional</h3>
            <div class="plan-price">
              <span class="price-amount">€24,99</span>
              <span class="price-period">/Monat</span>
            </div>
            <p class="plan-description">Für wachsende Unternehmen</p>
          </div>

          <v-divider></v-divider>

          <v-card-text class="features-list">
            <div class="feature-item">
              <v-icon color="success" size="small">mdi-check</v-icon>
              <span>Unbegrenzte Aufträge</span>
            </div>
            <div class="feature-item">
              <v-icon color="success" size="small">mdi-check</v-icon>
              <span>Erweiterte Statistiken & Berichte</span>
            </div>
            <div class="feature-item">
              <v-icon color="success" size="small">mdi-check</v-icon>
              <span>Priority Support (24h)</span>
            </div>
            <div class="feature-item">
              <v-icon color="success" size="small">mdi-check</v-icon>
              <span>Automatische Backups</span>
            </div>
            <div class="feature-item">
              <v-icon color="success" size="small">mdi-check</v-icon>
              <span>Erweiterte PDF-Vorlagen</span>
            </div>
            <div class="feature-item">
              <v-icon color="success" size="small">mdi-check</v-icon>
              <span>Kalender-Integration</span>
            </div>
            <div class="feature-item disabled">
              <v-icon color="grey" size="small">mdi-close</v-icon>
              <span>API Zugang</span>
            </div>
            <div class="feature-item disabled">
              <v-icon color="grey" size="small">mdi-close</v-icon>
              <span>White-Label Option</span>
            </div>
          </v-card-text>

          <v-card-actions class="pa-4">
            <v-btn
              variant="elevated"
              color="primary"
              block
              :disabled="currentPlan?.id === 'professional'"
              @click="selectPlan('professional')"
            >
              {{ currentPlan?.id === 'professional' ? 'Aktueller Plan' : 'Upgrade' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>

      <!-- Enterprise Plan -->
      <v-col cols="12" md="4">
        <v-card
          class="pricing-card enterprise-card"
          :class="{ 'current-plan-border': currentPlan?.id === 'enterprise' }"
        >
          <div class="card-header enterprise-header">
            <v-icon color="purple" size="40">mdi-domain</v-icon>
            <h3 class="plan-name">Enterprise</h3>
            <div class="plan-price">
              <span class="price-amount">€99,99</span>
              <span class="price-period">/Monat</span>
            </div>
            <p class="plan-description">Für große Organisationen</p>
          </div>

          <v-divider></v-divider>

          <v-card-text class="features-list">
            <div class="feature-item">
              <v-icon color="success" size="small">mdi-check</v-icon>
              <span>Alles aus Professional</span>
            </div>
            <div class="feature-item">
              <v-icon color="success" size="small">mdi-check</v-icon>
              <span>Multi-Standort Verwaltung</span>
            </div>
            <div class="feature-item">
              <v-icon color="success" size="small">mdi-check</v-icon>
              <span>Dedicated Account Manager</span>
            </div>
            <div class="feature-item">
              <v-icon color="success" size="small">mdi-check</v-icon>
              <span>REST API Vollzugang</span>
            </div>
            <div class="feature-item">
              <v-icon color="success" size="small">mdi-check</v-icon>
              <span>White-Label Branding</span>
            </div>
            <div class="feature-item">
              <v-icon color="success" size="small">mdi-check</v-icon>
              <span>SSO Integration</span>
            </div>
            <div class="feature-item">
              <v-icon color="success" size="small">mdi-check</v-icon>
              <span>Custom Entwicklung</span>
            </div>
            <div class="feature-item">
              <v-icon color="success" size="small">mdi-check</v-icon>
              <span>SLA Garantie (99,9%)</span>
            </div>
          </v-card-text>

          <v-card-actions class="pa-4">
            <v-btn
              variant="elevated"
              color="purple"
              block
              :disabled="currentPlan?.id === 'enterprise'"
              @click="selectPlan('enterprise')"
            >
              {{ currentPlan?.id === 'enterprise' ? 'Aktueller Plan' : 'Upgrade' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- Feature Comparison Table -->
    <v-card class="comparison-table mt-8">
      <v-card-title class="d-flex align-center">
        <v-icon color="primary" class="mr-2">mdi-compare</v-icon>
        Detaillierter Funktionsvergleich
      </v-card-title>

      <v-card-text>
        <v-table class="comparison-table-content">
          <thead>
            <tr>
              <th class="feature-column">Feature</th>
              <th class="plan-column">Basic</th>
              <th class="plan-column">Professional</th>
              <th class="plan-column">Enterprise</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="feature in comparisonFeatures" :key="feature.name">
              <td class="feature-name">{{ feature.name }}</td>
              <td class="plan-value">
                <v-icon v-if="feature.basic === true" color="success" size="small">mdi-check</v-icon>
                <v-icon v-else-if="feature.basic === false" color="grey" size="small">mdi-close</v-icon>
                <span v-else>{{ feature.basic }}</span>
              </td>
              <td class="plan-value">
                <v-icon v-if="feature.professional === true" color="success" size="small">mdi-check</v-icon>
                <v-icon v-else-if="feature.professional === false" color="grey" size="small">mdi-close</v-icon>
                <span v-else>{{ feature.professional }}</span>
              </td>
              <td class="plan-value">
                <v-icon v-if="feature.enterprise === true" color="success" size="small">mdi-check</v-icon>
                <v-icon v-else-if="feature.enterprise === false" color="grey" size="small">mdi-close</v-icon>
                <span v-else>{{ feature.enterprise }}</span>
              </td>
            </tr>
          </tbody>
        </v-table>
      </v-card-text>
    </v-card>

    <!-- Payment Security -->
    <v-card class="security-card mt-8">
      <v-card-title class="d-flex align-center">
        <v-icon color="success" class="mr-2">mdi-shield-check</v-icon>
        Sicher & Vertrauenswürdig
      </v-card-title>

      <v-card-text>
        <v-row align="center">
          <v-col cols="12" md="8">
            <p class="text-body-1 mb-3">
              Ihre Daten sind bei uns in sicheren Händen. Wir verwenden modernste Verschlüsselungstechnologien
              und sind DSGVO-konform.
            </p>
            <div class="security-features">
              <v-chip class="mr-2 mb-2" color="success" variant="outlined" size="small">
                <v-icon start size="small">mdi-lock</v-icon>
                SSL Verschlüsselung
              </v-chip>
              <v-chip class="mr-2 mb-2" color="success" variant="outlined" size="small">
                <v-icon start size="small">mdi-shield</v-icon>
                DSGVO Konform
              </v-chip>
              <v-chip class="mr-2 mb-2" color="success" variant="outlined" size="small">
                <v-icon start size="small">mdi-backup-restore</v-icon>
                Tägliche Backups
              </v-chip>
              <v-chip class="mr-2 mb-2" color="success" variant="outlined" size="small">
                <v-icon start size="small">mdi-clock-check</v-icon>
                99,9% Uptime
              </v-chip>
            </div>
          </v-col>
          <v-col cols="12" md="4" class="text-center">
            <div class="payment-methods">
              <div class="payment-icons">
                <v-icon size="32" color="primary" class="mr-2">mdi-credit-card</v-icon>
                <v-icon size="32" color="primary" class="mr-2">mdi-paypal</v-icon>
                <v-icon size="32" color="primary" class="mr-2">mdi-bank</v-icon>
              </div>
              <p class="text-caption mt-2">Sichere Zahlungsmethoden</p>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- FAQ Section -->
    <v-card class="faq-card mt-8">
      <v-card-title class="d-flex align-center">
        <v-icon color="info" class="mr-2">mdi-help-circle</v-icon>
        Häufig gestellte Fragen
      </v-card-title>

      <v-card-text>
        <v-expansion-panels variant="accordion">
          <v-expansion-panel v-for="faq in faqs" :key="faq.question">
            <v-expansion-panel-title>{{ faq.question }}</v-expansion-panel-title>
            <v-expansion-panel-text>{{ faq.answer }}</v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
    </v-card>

    <!-- Plan Selection Dialog -->
    <v-dialog v-model="showPlanDialog" max-width="600">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon :color="selectedPlanData?.headerColor" class="mr-2">{{ selectedPlanData?.icon }}</v-icon>
          {{ selectedPlanData?.name }} Plan aktivieren
        </v-card-title>

        <v-card-text>
          <div class="plan-summary">
            <div class="d-flex justify-space-between align-center mb-4">
              <span class="text-h6">{{ selectedPlanData?.name }}</span>
              <span class="text-h6">{{ selectedPlanData?.price }}</span>
            </div>

            <v-divider class="mb-4"></v-divider>

            <div class="billing-info">
              <v-select
                v-model="billingCycle"
                :items="billingOptions"
                label="Abrechnungszyklus"
                variant="outlined"
                density="compact"
              ></v-select>

              <div class="discount-info mt-2" v-if="billingCycle === 'yearly'">
                <v-alert type="success" variant="tonal" class="mb-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2">mdi-tag</v-icon>
                    2 Monate gratis bei jährlicher Zahlung!
                  </div>
                </v-alert>
              </div>
            </div>

            <div class="total-price mt-4">
              <div class="d-flex justify-space-between align-center">
                <span class="text-subtitle-1">Gesamtpreis:</span>
                <span class="text-h6 font-weight-bold">{{ calculateTotal() }}</span>
              </div>
              <p class="text-caption text-grey">
                {{ billingCycle === 'yearly' ? 'Jährlich abgerechnet' : 'Monatlich abgerechnet' }}
              </p>
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showPlanDialog = false">Abbrechen</v-btn>
          <v-btn
            :color="selectedPlanData?.headerColor"
            variant="elevated"
            @click="confirmPlanSelection"
            :loading="processingPayment"
          >
            {{ selectedPlanData?.id === 'basic' ? 'Aktivieren' : 'Jetzt kaufen' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success Dialog -->
    <v-dialog v-model="showSuccessDialog" max-width="500">
      <v-card>
        <v-card-text class="text-center pa-8">
          <v-icon size="64" color="success" class="mb-4">mdi-check-circle</v-icon>
          <h3 class="text-h5 mb-2">Erfolgreich aktiviert!</h3>
          <p class="text-body-1">
            Ihr {{ selectedPlanData?.name }} Plan wurde erfolgreich aktiviert.
            Sie können jetzt alle Premium-Features nutzen.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="success" variant="elevated" @click="showSuccessDialog = false">
            Verstanden
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useToast } from 'vue-toastification'

const toast = useToast()

// State
const showPlanDialog = ref(false)
const showSuccessDialog = ref(false)
const processingPayment = ref(false)
const selectedPlan = ref(null)
const billingCycle = ref('monthly')

// Mock current plan (in echt würde das von der API kommen)
const currentPlan = ref({
  id: 'basic',
  name: 'Basic',
  price: 'Kostenlos',
  description: 'Grundfunktionen für kleine Teams',
  color: 'grey',
  icon: 'mdi-account'
})

const nextBilling = ref('Kein Ablaufdatum')

// Plans data
const plans = {
  basic: {
    id: 'basic',
    name: 'Basic',
    price: 'Kostenlos',
    monthlyPrice: 0,
    yearlyPrice: 0,
    headerColor: 'grey',
    icon: 'mdi-account'
  },
  professional: {
    id: 'professional',
    name: 'Professional',
    price: '€24,99/Monat',
    monthlyPrice: 24.99,
    yearlyPrice: 249.99,
    headerColor: 'primary',
    icon: 'mdi-briefcase'
  },
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    price: '€99,99/Monat',
    monthlyPrice: 99.99,
    yearlyPrice: 999.99,
    headerColor: 'purple',
    icon: 'mdi-domain'
  }
}

// Billing options
const billingOptions = [
  { title: 'Monatlich', value: 'monthly' },
  { title: 'Jährlich (2 Monate gratis)', value: 'yearly' }
]

// Computed
const selectedPlanData = computed(() => {
  return selectedPlan.value ? plans[selectedPlan.value] : null
})

// Comparison features
const comparisonFeatures = ref([
  { name: 'Aufträge pro Monat', basic: '50', professional: 'Unbegrenzt', enterprise: 'Unbegrenzt' },
  { name: 'Benutzer', basic: '5', professional: '25', enterprise: 'Unbegrenzt' },
  { name: 'Speicherplatz', basic: '1 GB', professional: '50 GB', enterprise: '500 GB' },
  { name: 'Support', basic: 'Email', professional: '24h Priority', enterprise: 'Dedicated Manager' },
  { name: 'Erweiterte Berichte', basic: false, professional: true, enterprise: true },
  { name: 'API Zugang', basic: false, professional: false, enterprise: true },
  { name: 'White-Label', basic: false, professional: false, enterprise: true },
  { name: 'SSO Integration', basic: false, professional: false, enterprise: true },
  { name: 'Mobile App', basic: true, professional: true, enterprise: true },
  { name: 'Automatische Backups', basic: false, professional: true, enterprise: true }
])

// FAQs
const faqs = ref([
  {
    question: 'Kann ich meinen Plan jederzeit ändern?',
    answer: 'Ja, Sie können Ihren Plan jederzeit upgraden oder downgraden. Änderungen werden zum nächsten Abrechnungszeitraum wirksam.'
  },
  {
    question: 'Gibt es eine kostenlose Testversion?',
    answer: 'Der Basic Plan ist dauerhaft kostenlos. Professional und Enterprise Pläne können 30 Tage kostenlos getestet werden.'
  },
  {
    question: 'Sind meine Daten sicher?',
    answer: 'Ja, wir verwenden modernste Verschlüsselung und sind vollständig DSGVO-konform. Ihre Daten werden in deutschen Rechenzentren gespeichert.'
  },
  {
    question: 'Kann ich jederzeit kündigen?',
    answer: 'Ja, Sie können jederzeit mit einer Frist von 30 Tagen kündigen. Bei jährlicher Zahlung läuft der Service bis zum Ende der Laufzeit.'
  },
  {
    question: 'Welche Zahlungsmethoden werden akzeptiert?',
    answer: 'Wir akzeptieren alle gängigen Kreditkarten, PayPal, SEPA-Lastschrift und Überweisung.'
  }
])

// Methods
const selectPlan = (planId) => {
  if (planId === currentPlan.value?.id) return

  selectedPlan.value = planId
  showPlanDialog.value = true
}

const calculateTotal = () => {
  if (!selectedPlanData.value) return '€0,00'

  if (selectedPlanData.value.id === 'basic') {
    return 'Kostenlos'
  }

  const price = billingCycle.value === 'yearly'
    ? selectedPlanData.value.yearlyPrice
    : selectedPlanData.value.monthlyPrice

  const period = billingCycle.value === 'yearly' ? '/Jahr' : '/Monat'

  return `€${price.toFixed(2)}${period}`
}

const confirmPlanSelection = async () => {
  processingPayment.value = true

  // Simulate payment processing
  await new Promise(resolve => setTimeout(resolve, 2000))

  // Update current plan
  currentPlan.value = {
    id: selectedPlanData.value.id,
    name: selectedPlanData.value.name,
    price: selectedPlanData.value.price,
    description: getDescriptionForPlan(selectedPlanData.value.id),
    color: selectedPlanData.value.headerColor,
    icon: selectedPlanData.value.icon
  }

  // Update next billing
  if (selectedPlanData.value.id !== 'basic') {
    const nextDate = new Date()
    nextDate.setMonth(nextDate.getMonth() + (billingCycle.value === 'yearly' ? 12 : 1))
    nextBilling.value = nextDate.toLocaleDateString('de-DE')
  } else {
    nextBilling.value = 'Kein Ablaufdatum'
  }

  processingPayment.value = false
  showPlanDialog.value = false
  showSuccessDialog.value = true

  toast.success(`${selectedPlanData.value.name} Plan erfolgreich aktiviert!`)
}

const getDescriptionForPlan = (planId) => {
  const descriptions = {
    basic: 'Grundfunktionen für kleine Teams',
    professional: 'Erweiterte Features für wachsende Unternehmen',
    enterprise: 'Vollständige Lösung für große Organisationen'
  }
  return descriptions[planId] || ''
}
</script>

<style scoped>
.subscription-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
}

.pricing-header {
  text-align: center;
  margin-bottom: 48px;
}

.pricing-title {
  font-size: 2.5rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pricing-subtitle {
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.7);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

.current-plan-card {
  background: rgba(25, 25, 30, 0.9) !important;
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 12px;
}

.pricing-cards {
  margin-bottom: 48px;
}

.pricing-card {
  background: rgba(25, 25, 30, 0.9) !important;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  position: relative;
  height: 100%;
  overflow: hidden;
}

.pricing-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  border-color: rgba(33, 150, 243, 0.5);
}

.professional-card:hover {
  border-color: rgba(33, 150, 243, 0.7);
  box-shadow: 0 20px 40px rgba(33, 150, 243, 0.2);
}

.enterprise-card:hover {
  border-color: rgba(156, 39, 176, 0.7);
  box-shadow: 0 20px 40px rgba(156, 39, 176, 0.2);
}

.current-plan-border {
  border: 2px solid rgba(76, 175, 80, 0.6) !important;
  box-shadow: 0 0 20px rgba(76, 175, 80, 0.3);
}

.popular-badge {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
}

.card-header {
  text-align: center;
  padding: 32px 24px;
  position: relative;
}

.basic-header {
  background: linear-gradient(135deg, rgba(96, 125, 139, 0.1), rgba(96, 125, 139, 0.05));
}

.professional-header {
  background: linear-gradient(135deg, rgba(33, 150, 243, 0.15), rgba(33, 150, 243, 0.05));
}

.enterprise-header {
  background: linear-gradient(135deg, rgba(156, 39, 176, 0.15), rgba(156, 39, 176, 0.05));
}

.plan-name {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 16px 0 8px;
  color: rgba(255, 255, 255, 0.95);
}

.plan-price {
  margin: 16px 0;
}

.price-amount {
  font-size: 2.5rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
}

.price-period {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.6);
  margin-left: 4px;
}

.plan-description {
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
  font-size: 0.95rem;
}

.features-list {
  padding: 24px;
}

.feature-item {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  font-size: 0.95rem;
}

.feature-item.disabled {
  opacity: 0.5;
}

.feature-item .v-icon {
  margin-right: 12px;
  min-width: 20px;
}

.comparison-table {
  background: rgba(25, 25, 30, 0.9) !important;
  border-radius: 16px;
}

.comparison-table-content {
  background: transparent;
}

.comparison-table-content th {
  background: rgba(33, 150, 243, 0.1);
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.comparison-table-content td {
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.8);
}

.feature-column {
  width: 40%;
}

.plan-column {
  width: 20%;
  text-align: center;
}

.feature-name {
  font-weight: 500;
}

.plan-value {
  text-align: center;
}

.security-card, .faq-card {
  background: rgba(25, 25, 30, 0.9) !important;
  border-radius: 16px;
}

.security-features {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.payment-methods {
  text-align: center;
}

.payment-icons {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 8px;
}

.plan-summary {
  background: rgba(30, 30, 40, 0.5);
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 16px;
}

.billing-info {
  margin: 20px 0;
}

.discount-info {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.8; }
}

.total-price {
  background: rgba(33, 150, 243, 0.1);
  padding: 16px;
  border-radius: 8px;
  border: 1px solid rgba(33, 150, 243, 0.3);
}

/* Responsive Design */
@media (max-width: 960px) {
  .pricing-title {
    font-size: 2rem;
    flex-direction: column;
    gap: 8px;
  }

  .pricing-subtitle {
    font-size: 1.1rem;
  }

  .card-header {
    padding: 24px 16px;
  }

  .features-list {
    padding: 16px;
  }
}

@media (max-width: 600px) {
  .subscription-container {
    padding: 16px;
  }

  .pricing-title {
    font-size: 1.8rem;
  }

  .price-amount {
    font-size: 2rem;
  }

  .comparison-table-content th,
  .comparison-table-content td {
    padding: 8px;
    font-size: 0.85rem;
  }

  .feature-column {
    width: 50%;
  }

  .plan-column {
    width: 16.67%;
  }
}
</style>
